/**
 * An organization's own view of its bill.
 *
 * Deliberately separate from `adminBilling.ts`, which is the system admin's vocabulary: plans,
 * contacts, reminder ledgers, Stripe ids. The customer side is one invoice and what to do about it,
 * and keeping the two files apart is what stops a staff-only field drifting into a screen a coach
 * can open.
 */

/** `GET /api/billing/me` and `POST /api/billing/me/dismiss`. */
export interface MyBillingNotice {
  /**
   * Whether there is anything to show. False for an organization with no billing set up, nothing
   * outstanding, a paused one, or one where this person has put away every unpaid invoice that is
   * not yet overdue. Every other field is null or zero when this is false.
   */
  hasBill: boolean;

  /** The invoice the notice is about — what the dismiss call takes. */
  invoiceId: string | null;

  /** The reference to quote. Null on a Stripe invoice Stripe has not numbered yet. */
  number: string | null;

  /** The invoice's face value, in minor units. Formatting is ours. */
  amountCents: number | null;

  /**
   * What is still owed: the face value less anything already received.
   *
   * **This is what the banner says, not `amountCents`.** A part payment leaves the invoice open
   * with a smaller balance, and quoting the whole bill to a program that has already paid half of
   * it is an argument nobody needs to have.
   */
  amountDueCents: number | null;

  /** ISO 4217, lower case from the API. */
  currency: string | null;

  /** `yyyy-MM-dd`. A due date is a day, not an instant. */
  dueOn: string | null;

  /** Past due and still owed. Derived by the server, never stored. */
  isOverdue: boolean;

  /** Whole days past due, 0 when it is not overdue. */
  daysOverdue: number;

  /** This person has already put it away. True here means it came back, which only overdue does. */
  isDismissed: boolean;

  /**
   * Whether putting it away would actually silence it. False once overdue, and the banner shows no
   * dismiss control rather than one that does nothing.
   */
  canDismiss: boolean;

  /** Stripe's hosted invoice page. Null unless Stripe has finalized the invoice. */
  hostedInvoiceUrl: string | null;
}

/** What the banner shows when there is nothing to show. */
export const NO_BILLING_NOTICE: MyBillingNotice = {
  hasBill: false,
  invoiceId: null,
  number: null,
  amountCents: null,
  amountDueCents: null,
  currency: null,
  dueOn: null,
  isOverdue: false,
  daysOverdue: 0,
  isDismissed: false,
  canDismiss: false,
  hostedInvoiceUrl: null,
};

/**
 * One of the organization's own invoices, as its admins see it.
 *
 * Deliberately smaller than the staff view: no plan, no creator, no internal notes, no reminder
 * history. A customer is being shown their own bill, not the row this system keeps about them.
 */
export interface MyInvoice {
  id: string;
  /** Null on a Stripe invoice that has not been numbered yet. */
  number: string | null;
  issuedOn: string;
  dueOn: string;
  amountCents: number;
  /** What is still owed. Zero once settled. */
  amountRemainingCents: number;
  currency: string;
  /** Derived by the API, so this screen and the staff screen cannot disagree about "overdue". */
  displayStatus: 'open' | 'draft' | 'overdue' | 'paid' | 'void' | 'uncollectible';
  daysOverdue: number;
  paidAt: string | null;
  /**
   * Where to pay, or null. Only ever set for an invoice Stripe holds: one raised here is collected
   * by cheque, wire or ACH, so a Pay button on it would lead nowhere.
   */
  payUrl: string | null;
  pdfUrl: string | null;
}

/** The organization's invoices, newest first, with what it currently owes. */
export interface MyInvoices {
  items: MyInvoice[];
  outstandingCents: number;
  currency: string;
  /** Stripe holds this organization's billing, so the pay links are real. */
  isStripeManaged: boolean;
}

/** What an organization with no billing at all looks like. Never an error. */
export const NO_INVOICES: MyInvoices = {
  items: [],
  outstandingCents: 0,
  currency: 'USD',
  isStripeManaged: false,
};
